import { z } from "zod";

// Single source of truth for password rules when CREATING or CHANGING a
// password (signup, password reset, change-password). These mirror the
// Supabase Auth server policy (Cloud → Users → Auth → Email settings):
//   • minimum 8 characters
//   • must include a lowercase letter, an uppercase letter, and a number
//
// The server ALSO runs a HaveIBeenPwned breach check, which we can't
// replicate client-side. If a password is in a known breach the server
// rejects it with its own message; callers surface that raw message.
//
// NOTE: this is intentionally NOT used for the sign-IN form. Accounts created
// under the older 6-character policy must still be able to log in, so sign-in
// validation stays lenient and only the server decides if credentials match.
//
// The UI (PasswordStrengthIndicator) renders its checklist directly from
// PASSWORD_REQUIREMENTS below, so the meter and the validator can never drift.

export const PASSWORD_MIN_LENGTH = 8;

/** Human-readable summary shown under password fields. */
export const PASSWORD_REQUIREMENTS_TEXT =
  "At least 8 characters, including an uppercase letter, a lowercase letter, and a number.";

export interface PasswordRequirement {
  id: string;
  /** Short label used in the checklist UI. */
  label: string;
  /** Message surfaced when this requirement is the first unmet one. */
  error: string;
  test: (password: string) => boolean;
}

/**
 * The mandatory rules, in the order they are validated and displayed.
 * Adding a rule here automatically adds it to validation AND to the meter.
 */
export const PASSWORD_REQUIREMENTS: readonly PasswordRequirement[] = [
  {
    id: "length",
    label: `At least ${PASSWORD_MIN_LENGTH} characters`,
    error: `Password must be at least ${PASSWORD_MIN_LENGTH} characters`,
    test: (p) => p.length >= PASSWORD_MIN_LENGTH,
  },
  {
    id: "lowercase",
    label: "A lowercase letter",
    error: "Password must include a lowercase letter",
    test: (p) => /[a-z]/.test(p),
  },
  {
    id: "uppercase",
    label: "An uppercase letter",
    error: "Password must include an uppercase letter",
    test: (p) => /[A-Z]/.test(p),
  },
  {
    id: "digit",
    label: "A number",
    error: "Password must include a number",
    test: (p) => /\d/.test(p),
  },
];

/** Optional bonus, never required to submit. */
export const PASSWORD_BONUS_LABEL = "A symbol (optional, adds strength)";

export function hasSymbol(password: string): boolean {
  return /[^a-zA-Z0-9]/.test(password);
}

export interface PasswordEvaluation {
  requirements: Array<{ id: string; label: string; met: boolean }>;
  allRequiredMet: boolean;
  metCount: number;
  hasSymbol: boolean;
}

/**
 * Structured view of a password against the policy. Used by the strength
 * indicator so the checklist is generated from the same rules as validation.
 */
export function evaluatePassword(password: string): PasswordEvaluation {
  const requirements = PASSWORD_REQUIREMENTS.map((req) => ({
    id: req.id,
    label: req.label,
    met: req.test(password),
  }));
  const metCount = requirements.filter((r) => r.met).length;
  return {
    requirements,
    allRequiredMet: metCount === requirements.length,
    metCount,
    hasSymbol: hasSymbol(password),
  };
}

/**
 * Returns the first unmet requirement as a user-facing message, or null if the
 * password satisfies the client-side policy. Used by non-zod call sites
 * (toast-based forms) so they validate identically to the zod schema.
 */
export function getPasswordError(password: string): string | null {
  for (const req of PASSWORD_REQUIREMENTS) {
    if (!req.test(password)) return req.error;
  }
  return null;
}

/** Zod schema for forms built on zod (e.g. the Auth page). */
export const passwordSchema = z.string().superRefine((value, ctx) => {
  for (const req of PASSWORD_REQUIREMENTS) {
    if (!req.test(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: req.error });
      return;
    }
  }
});
